import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  AreaChart, Area, XAxis, YAxis, Tooltip, 
  BarChart, Bar, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import { 
  TrendingUp, TrendingDown, PieChart as PieIcon, 
  BarChart3, Calendar, Target, Shield,
  Car, Palette, Wine, Gem, ShoppingBag, Watch
} from 'lucide-react';

const CATEGORY_COLORS = {
  car: '#10B981',      // Emerald
  art: '#8B5CF6',      // Purple
  wine: '#EF4444',     // Red
  jewelry: '#F59E0B',  // Amber
  handbag: '#F97316',  // Orange
  watch: '#06B6D4',    // Cyan
};

const CATEGORY_ICONS = {
  car: Car,
  art: Palette,
  wine: Wine,
  jewelry: Gem,
  handbag: ShoppingBag,
  watch: Watch,
};

const CustomTooltip = ({ active, payload, label, formatCurrency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1A1A] border border-[#333] p-3 shadow-lg">
        <p className="text-white text-sm font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' && entry.value > 100 
              ? formatCurrency(entry.value) 
              : `${entry.value}%`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const PortfolioAnalytics = ({ summary, assets, performance }) => {
  const { formatCurrency } = useCurrency();
  const [activeView, setActiveView] = useState('allocation');
  const [valueHistory, setValueHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('monthly');

  useEffect(() => {
    fetchHistory(timeRange);
  }, [timeRange]);

  const fetchHistory = async (range) => {
    setHistoryLoading(true);
    try {
      const res = await api.getPortfolioHistory(range);
      const points = (res.data?.points || []).map(p => ({ label: p.label, value: p.value, date: p.date }));
      setValueHistory(points);
    } catch (e) {
      console.error('history error', e);
      setValueHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Prepare allocation data for pie chart
  // API returns 'categories', not 'category_breakdown'
  const allocationData = (summary?.category_breakdown || summary?.categories)
    ? Object.entries(summary.category_breakdown || summary.categories).map(([category, data]) => ({
        name: category.charAt(0).toUpperCase() + category.slice(1),
        value: data.value,
        count: data.count,
        color: CATEGORY_COLORS[category] || '#666',
        category: category
      }))
    : [];

  // Calculate diversification score (0-100)
  const calculateDiversificationScore = () => {
    if (allocationData.length === 0) return 0;
    if (allocationData.length === 1) return 20;
    
    const totalValue = allocationData.reduce((sum, d) => sum + d.value, 0);
    const percentages = allocationData.map(d => (d.value / totalValue) * 100);
    
    // Ideal would be equal distribution
    const idealPercentage = 100 / 8; // 8 categories
    const variance = percentages.reduce((sum, p) => sum + Math.abs(p - idealPercentage), 0);
    
    // Score based on how close to ideal distribution (lower variance = higher score)
    const score = Math.max(0, 100 - variance);
    return Math.round(score);
  };

  // Risk assessment based on concentration
  const getRiskLevel = () => {
    if (allocationData.length === 0) return { level: 'N/A', color: 'text-muted-foreground' };
    
    const totalValue = allocationData.reduce((sum, d) => sum + d.value, 0);
    const maxConcentration = Math.max(...allocationData.map(d => (d.value / totalValue) * 100));
    
    if (maxConcentration > 70) return { level: 'High', color: 'text-red-400', description: 'Portfolio heavily concentrated' };
    if (maxConcentration > 50) return { level: 'Medium', color: 'text-amber-400', description: 'Moderate concentration' };
    return { level: 'Low', color: 'text-emerald-400', description: 'Well diversified' };
  };

  const diversificationScore = calculateDiversificationScore();
  const risk = getRiskLevel();

  if (!summary || !assets || assets.length === 0) {
    return (
      <div className="luxury-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={20} className="text-gold" />
          <h2 className="font-serif text-xl text-white">Portfolio Analytics</h2>
        </div>
        <p className="text-muted-foreground text-center py-8">
          Add assets to view portfolio analytics
        </p>
      </div>
    );
  }

  return (
    <div className="luxury-card overflow-hidden" data-testid="portfolio-analytics">
      {/* Header with tabs */}
      <div className="p-4 border-b border-[#1A1A1A] flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <BarChart3 size={20} className="text-gold" />
          <h2 className="font-serif text-xl text-white">Portfolio Analytics</h2>
        </div>
        <div className="flex gap-2">
          {[
            { id: 'allocation', label: 'Allocation', icon: PieIcon },
            { id: 'history', label: 'History', icon: Calendar },
            { id: 'metrics', label: 'Metrics', icon: Target },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                activeView === tab.id 
                  ? 'bg-gold text-black' 
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Allocation View - Pie Chart */}
        {activeView === 'allocation' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={allocationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {allocationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Legend & Breakdown */}
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">Asset Allocation by Category</p>
              {allocationData.map((item) => {
                const Icon = CATEGORY_ICONS[item.category];
                const totalValue = allocationData.reduce((sum, d) => sum + d.value, 0);
                const percentage = ((item.value / totalValue) * 100).toFixed(1);
                return (
                  <div 
                    key={item.category}
                    className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1A1A1A]"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      />
                      {Icon && <Icon size={16} style={{ color: item.color }} />}
                      <span className="text-white text-sm">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm font-mono">{formatCurrency(item.value)}</p>
                      <p className="text-xs text-muted-foreground">{percentage}% • {item.count} asset{item.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* History View - Area Chart */}
        {activeView === 'history' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">Portfolio Value Over Time</p>
              <div className="flex gap-1 bg-[#0A0A0A] border border-[#262626] p-1 rounded">
                {[
                  { id: 'weekly', label: '1W' },
                  { id: 'monthly', label: '1M' },
                  { id: 'yearly', label: '1Y' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setTimeRange(opt.id)}
                    data-testid={`chart-range-${opt.id}`}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                      timeRange === opt.id
                        ? 'bg-gold text-black font-medium'
                        : 'text-muted-foreground hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-[300px]">
              {historyLoading ? (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">Loading history...</div>
              ) : valueHistory.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No history available - add an asset to populate this chart.</div>
              ) : (() => {
                const values = valueHistory.map(p => p.value);
                const start = values[0] || 0;
                const end = values[values.length - 1] || 0;
                const minV = Math.min(...values);
                const maxV = Math.max(...values);
                const change = end - start;
                const changePct = start > 0 ? (change / start) * 100 : 0;
                const positive = change >= 0;
                const lineColor = positive ? '#10B981' : '#EF4444';
                const fillColor = positive ? '#10B981' : '#EF4444';

                return (
                  <div className="h-full flex flex-col">
                    {/* Stats bar */}
                    <div className="flex flex-wrap items-end justify-between gap-3 mb-3 pb-3 border-b border-[#1A1A1A]">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Period change</p>
                        <p className={`text-2xl font-serif ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
                          {positive ? '+' : ''}{formatCurrency(change)}
                          <span className="text-sm ml-2">({positive ? '+' : ''}{changePct.toFixed(2)}%)</span>
                        </p>
                      </div>
                      <div className="flex gap-4 text-right">
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">High</p>
                          <p className="text-xs text-emerald-400">{formatCurrency(maxV)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Low</p>
                          <p className="text-xs text-red-400">{formatCurrency(minV)}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={valueHistory} margin={{ top: 10, right: 10, bottom: 5, left: 0 }}>
                          <defs>
                            <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={fillColor} stopOpacity={0.4}/>
                              <stop offset="95%" stopColor={fillColor} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid stroke="#1A1A1A" strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="label"
                            stroke="#444"
                            tick={{ fill: '#999', fontSize: 11 }}
                            axisLine={{ stroke: '#222' }}
                            interval={timeRange === 'weekly' ? 1 : 'preserveStartEnd'}
                          />
                          <YAxis
                            stroke="#444"
                            tick={{ fill: '#999', fontSize: 11 }}
                            axisLine={{ stroke: '#222' }}
                            domain={[
                              (dataMin) => Math.floor(dataMin * 0.985),
                              (dataMax) => Math.ceil(dataMax * 1.015),
                            ]}
                            tickFormatter={(value) => value >= 1000000 ? `${(value / 1000000).toFixed(2)}M` : `${(value / 1000).toFixed(0)}k`}
                            width={60}
                          />
                          <ReferenceLine y={start} stroke="#C9A962" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: 'Start', fill: '#C9A962', fontSize: 10, position: 'insideTopLeft' }} />
                          <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke={lineColor}
                            strokeWidth={2}
                            fill="url(#valueGradient)"
                            name="Portfolio Value"
                            activeDot={{ r: 4, fill: lineColor, stroke: '#0A0A0A', strokeWidth: 2 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Metrics View */}
        {activeView === 'metrics' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Diversification Score */}
              <div className="p-5 bg-[#0A0A0A] border border-[#1A1A1A]">
                <div className="flex items-center gap-2 mb-3">
                  <PieIcon size={18} className="text-gold" />
                  <span className="text-sm text-muted-foreground">Diversification Score</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-bold text-white">{diversificationScore}</span>
                  <span className="text-muted-foreground text-sm mb-1">/100</span>
                </div>
                <div className="mt-3 h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500"
                    style={{ width: `${diversificationScore}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {diversificationScore >= 70 ? 'Well diversified across categories' : 
                   diversificationScore >= 40 ? 'Moderate, consider adding new categories' : 
                   'Heavily concentrated, diversification recommended'}
                </p>
              </div>

              {/* Risk Level */}
              <div className="p-5 bg-[#0A0A0A] border border-[#1A1A1A]">
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={18} className="text-gold" />
                  <span className="text-sm text-muted-foreground">Concentration Risk</span>
                </div>
                <div className={`text-4xl font-bold ${risk.color}`}>
                  {risk.level}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {risk.description}
                </p>
                {risk.topCategory && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Largest: {risk.topCategory} ({risk.topPercentage}%)
                  </p>
                )}
              </div>

              {/* Performance Summary */}
              <div className="p-5 bg-[#0A0A0A] border border-[#1A1A1A]">
                <div className="flex items-center gap-2 mb-3">
                  {summary?.gain_percentage >= 0 ? (
                    <TrendingUp size={18} className="text-emerald-400" />
                  ) : (
                    <TrendingDown size={18} className="text-red-400" />
                  )}
                  <span className="text-sm text-muted-foreground">Overall Performance</span>
                </div>
                <div className={`text-4xl font-bold ${summary?.gain_percentage >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {summary?.gain_percentage >= 0 ? '+' : ''}{summary?.gain_percentage?.toFixed(1) || 0}%
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {formatCurrency(summary?.total_gain || 0)} total gain/loss
                </p>
              </div>
            </div>

            {diversificationScore < 50 && (() => {
              const missingCategories = ['art', 'watch', 'wine', 'jewelry', 'car', 'handbag']
                .filter(c => !allocationData.find(a => a.category === c));
              const categoryLabels = { art: 'Fine Art', watch: 'Watches', wine: 'Wine & Spirits', jewelry: 'Jewellery', car: 'Cars', handbag: 'Handbags' };
              const suggestions = missingCategories.slice(0, 3).map(c => categoryLabels[c]);
              return (
                <div className="p-5 bg-amber-400/5 border border-amber-400/20">
                  <p className="text-sm text-amber-400 leading-relaxed">
                    Your portfolio is heavily weighted in {allocationData.sort((a, b) => b.value - a.value)[0]?.name}.{' '}
                    This is also flagged as a concentration risk on your <span className="text-white font-medium">Risk page</span>{' '}
                    where you can adjust the threshold and see the assets responsible.
                    {suggestions.length > 0 && (
                      <>
                        {' '}To spread exposure, consider adding assets in{' '}
                        <span className="text-white font-medium">{suggestions.join(', ')}</span>.
                      </>
                    )}
                    {' '}If a single asset is the issue, use the{' '}
                    <span className="text-gold font-medium">"Fix this"</span> button on the Risk page to draft a
                    rebalancing brief to your wealth manager.
                  </p>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};
